import type { MarginTerm, MarginUploadItem } from "@yutai/shared"
import { parseCsvLine } from "../lists/rakutenCsv.js"

// 楽天証券「一般信用売建可能銘柄」CSV（MarginorderAJP_YYYYMMDD_N.csv）のパーサ。
// 列: 銘柄名, 銘柄コード, 市場, 弁済期限(いちにち), 弁済期限(14日), 弁済期限(無期限), 売建可能数量[株], ...
// 弁済期限列は対象なら "○"、対象外なら "-"。

export const MARGIN_TERM_LABELS: Record<MarginTerm, string> = {
  "14d": "14日",
  unlimited: "無期限",
}

const TERM_HEADER_KEYWORDS: Record<MarginTerm, string> = {
  "14d": "14日",
  unlimited: "無期限",
}

const CODE_RE = /^[0-9A-Z]{4}$/

export interface MarginCsvRow {
  code: string
  quantity: number
  terms: MarginTerm[]
  /** クロス費用の計算用。列が無い・読めない値は省略 */
  market: Omit<MarginUploadItem, "code" | "quantity">
}

export interface ParsedMarginCsv {
  rows: MarginCsvRow[]
  /** 全行で○が付いている弁済期限がちょうど1つならそれ（=その期限で絞り込んだCSV）。判別不能なら null */
  detectedTerm: MarginTerm | null
}

function toHalfWidth(s: string): string {
  return s.replace(/[０-９Ａ-Ｚ]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
}

/** "1,411" / "2,816.5" → number。数値でなければ null */
function parseNumber(raw: string | undefined): number | null {
  if (raw === undefined) return null
  const n = Number(raw.replace(/,/g, "").trim())
  return raw.trim() !== "" && Number.isFinite(n) ? n : null
}

/** 在庫CSVとして読めなければ Error を投げる */
export function parseMarginInventoryCsv(text: string): ParsedMarginCsv {
  const lines = text
    .replace(/^﻿/, "")
    .split(/\r?\n/)
    .filter((l) => l.trim() !== "")
  if (lines.length === 0) throw new Error("CSVが空です")

  const header = parseCsvLine(lines[0])
  const codeIdx = header.findIndex((h) => h.includes("銘柄コード"))
  const qtyIdx = header.findIndex((h) => h.includes("売建可能数量"))
  if (codeIdx < 0 || qtyIdx < 0) {
    throw new Error(
      "「銘柄コード」「売建可能数量」列が見つかりません。楽天証券の一般信用売建可能銘柄CSVを選択してください。"
    )
  }
  const colIdx = (keyword: string) => header.findIndex((h) => h.includes(keyword))
  const priceIdx = colIdx("現在値")
  const specialFeeIdx = colIdx("特別空売り料")
  const reverseFeeIdx = colIdx("逆日歩")
  const rightsIdx = colIdx("優待権利付き最終日")
  const kindIdx = colIdx("貸借区分")
  const termIdx = Object.fromEntries(
    (Object.keys(TERM_HEADER_KEYWORDS) as MarginTerm[]).map((t) => [
      t,
      header.findIndex((h) => h.includes("弁済期限") && h.includes(TERM_HEADER_KEYWORDS[t])),
    ])
  ) as Record<MarginTerm, number>

  const rows: MarginCsvRow[] = []
  for (const line of lines.slice(1)) {
    const cols = parseCsvLine(line)
    const code = toHalfWidth(cols[codeIdx]?.trim() ?? "")
    if (!CODE_RE.test(code)) continue
    const quantity = Number((cols[qtyIdx] ?? "").replace(/,/g, "").trim())
    if (!Number.isInteger(quantity) || quantity < 0) continue
    const terms = (Object.keys(termIdx) as MarginTerm[]).filter(
      (t) => termIdx[t] >= 0 && cols[termIdx[t]]?.trim() === "○"
    )
    const market: MarginCsvRow["market"] = {}
    const price = parseNumber(cols[priceIdx])
    if (price !== null && price > 0) market.price = price
    const specialFee = parseNumber(cols[specialFeeIdx])
    if (specialFee !== null && specialFee >= 0) market.specialFee = specialFee
    const reverseFee = parseNumber(cols[reverseFeeIdx])
    if (reverseFee !== null && reverseFee >= 0) market.reverseFee = reverseFee
    const rights = cols[rightsIdx]?.trim()
    if (rights && /^\d{2}\/\d{2}$/.test(rights)) market.rightsDate = rights
    const kind = cols[kindIdx]?.trim()
    if (kind === "貸借" || kind === "信用") market.kind = kind
    rows.push({ code, quantity, terms, market })
  }
  if (rows.length === 0) throw new Error("銘柄の行を読み取れませんでした")

  const allMarked = (Object.keys(termIdx) as MarginTerm[]).filter((t) =>
    rows.every((r) => r.terms.includes(t))
  )
  return { rows, detectedTerm: allMarked.length === 1 ? allMarked[0] : null }
}

/** 指定の弁済期限で売建可能（○）な行だけ。○列が無いCSVの場合は全行 */
export function itemsForTerm(parsed: ParsedMarginCsv, term: MarginTerm): MarginUploadItem[] {
  const hasTermColumns = parsed.rows.some((r) => r.terms.length > 0)
  return parsed.rows
    .filter((r) => !hasTermColumns || r.terms.includes(term))
    .map(({ code, quantity, market }) => ({ code, quantity, ...market }))
}

function jstDate(ms: number): string {
  return new Date(ms + 9 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

/**
 * スナップショットの日付。ブラウザからはファイルの作成日を読めないので、
 * ファイル名の日付（MarginorderAJP_20261001_2.csv → 2026-10-01）を優先し、
 * 無ければ最終更新日時（JST）を使う。
 */
export function inferSnapshotDate(filename: string, lastModified: number): string {
  const m = filename.match(/(20\d{2})(\d{2})(\d{2})/)
  if (m) {
    const date = `${m[1]}-${m[2]}-${m[3]}`
    const d = new Date(`${date}T00:00:00Z`)
    if (!Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === date) return date
  }
  return jstDate(lastModified)
}
